import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X, BarChart3, TrendingUp, ShieldAlert, Sparkles, CheckCircle2,
  Calendar, Zap, Info, ArrowRight, Play
} from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend
} from 'recharts';

/**
 * WhatIfSimulatorModal (NousMetrics v2.5 / v3.0)
 * Permite al atleta y al entrenador proyectar trayectorias de carga (CTL, ATL, TSB)
 * y Readiness futuro ante diferentes escenarios de entrenamiento (A/B/C).
 * 100% Compatible con iOS WebKit / Safari (sin mix-blend-mode).
 */
export default function WhatIfSimulatorModal({
  isOpen,
  onClose,
  currentCtl = 0,
  currentAtl = 0,
  currentTsb = 0,
  currentReadiness = 75
}) {
  const [activeScenario, setActiveScenario] = useState('B'); // 'A', 'B', 'C'
  const [horizonDays, setHorizonDays] = useState(14); // 7, 14, 30

  // Constantes de decaimiento Banister / Coggan
  const CTL_DECAY = Math.exp(-1 / 42);
  const ATL_DECAY = Math.exp(-1 / 7);

  // Escenarios predefinidos
  const scenarioDefinitions = {
    A: {
      name: 'Escenario A: Descanso & Supercompensación',
      badge: 'Regeneración',
      color: '#10b981',
      desc: 'Días de descanso total o paseos activos muy suaves (0 a 15 TSS/día). Ideal para rebote de TSB y recuperación parasimpática.',
      dailyTss: 10,
      dailyMech: 5
    },
    B: {
      name: 'Escenario B: Mantenimiento Aeróbico Controlado',
      badge: 'Base Z2',
      color: '#38bdf8',
      desc: 'Rodajes y carreras suaves en Zona 2 estrictamente aeróbica (~45-55 TSS/día). Mantiene el CTL sin disparar la fatiga aguda.',
      dailyTss: 50,
      dailyMech: 35
    },
    C: {
      name: 'Escenario C: Bloque de Sobrecarga e Intensidad',
      badge: 'Carga Alta',
      color: '#f59e0b',
      desc: 'Sesiones de series VT2, subidas y alto volumen (~90-110 TSS/día). Dispara el CTL pero acumula fatiga aguda negativa en TSB.',
      dailyTss: 95,
      dailyMech: 75
    }
  };

  // Cálculo matemático determinista de la proyección
  const simulationData = useMemo(() => {
    const scA = scenarioDefinitions.A;
    const scB = scenarioDefinitions.B;
    const scC = scenarioDefinitions.C;

    const baseCtl = Number(currentCtl) || 0;
    const baseAtl = Number(currentAtl) || 0;

    const dataPoints = [];
    let ctlA = baseCtl, atlA = baseAtl;
    let ctlB = baseCtl, atlB = baseAtl;
    let ctlC = baseCtl, atlC = baseAtl;

    // Punto día 0 (hoy)
    dataPoints.push({
      day: 'Hoy',
      dayNum: 0,
      tsbA: parseFloat((ctlA - atlA).toFixed(1)),
      tsbB: parseFloat((ctlB - atlB).toFixed(1)),
      tsbC: parseFloat((ctlC - atlC).toFixed(1)),
      ctlA: parseFloat(ctlA.toFixed(1)),
      ctlB: parseFloat(ctlB.toFixed(1)),
      ctlC: parseFloat(ctlC.toFixed(1)),
      atlA: parseFloat(atlA.toFixed(1)),
      atlB: parseFloat(atlB.toFixed(1)),
      atlC: parseFloat(atlC.toFixed(1))
    });

    for (let day = 1; day <= horizonDays; day++) {
      ctlA = (ctlA * CTL_DECAY) + (scA.dailyTss * (1 - CTL_DECAY));
      atlA = (atlA * ATL_DECAY) + (scA.dailyTss * (1 - ATL_DECAY));

      ctlB = (ctlB * CTL_DECAY) + (scB.dailyTss * (1 - CTL_DECAY));
      atlB = (atlB * ATL_DECAY) + (scB.dailyTss * (1 - ATL_DECAY));

      ctlC = (ctlC * CTL_DECAY) + (scC.dailyTss * (1 - CTL_DECAY));
      atlC = (atlC * ATL_DECAY) + (scC.dailyTss * (1 - ATL_DECAY));

      dataPoints.push({
        day: `+${day}d`,
        dayNum: day,
        tsbA: parseFloat((ctlA - atlA).toFixed(1)),
        tsbB: parseFloat((ctlB - atlB).toFixed(1)),
        tsbC: parseFloat((ctlC - atlC).toFixed(1)),
        ctlA: parseFloat(ctlA.toFixed(1)),
        ctlB: parseFloat(ctlB.toFixed(1)),
        ctlC: parseFloat(ctlC.toFixed(1)),
        atlA: parseFloat(atlA.toFixed(1)),
        atlB: parseFloat(atlB.toFixed(1)),
        atlC: parseFloat(atlC.toFixed(1))
      });
    }

    return dataPoints;
  }, [currentCtl, currentAtl, horizonDays]);

  const selectedScenario = scenarioDefinitions[activeScenario];
  const finalPoint = simulationData[simulationData.length - 1];

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const modalNode = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(2, 6, 23, 0.88)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        boxSizing: 'border-box'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#0b1120',
          border: '1px solid rgba(255, 255, 255, 0.16)',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '820px',
          maxHeight: '92vh',
          overflowY: 'auto',
          padding: '1.5rem',
          boxSizing: 'border-box',
          boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.95), 0 0 0 1px rgba(255, 255, 255, 0.08)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: 'rgba(56, 189, 248, 0.15)',
              border: '1px solid rgba(56, 189, 248, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <BarChart3 size={20} color="#38bdf8" />
            </div>
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#f8fafc' }}>
                Simulador What-If de Entrenamiento
              </div>
              <div style={{ fontSize: '0.74rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                Proyección de Balance de Fatiga (TSB), Fitness (CTL) y Carga Aguda (ATL)
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.06)',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255, 255, 255, 0.7)',
              cursor: 'pointer'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Controles de Escenario y Horizonte */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.25rem' }}>
          {/* Tabs Escenarios */}
          <div style={{ display: 'flex', gap: '6px' }}>
            {Object.entries(scenarioDefinitions).map(([key, sc]) => {
              const isActive = activeScenario === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveScenario(key)}
                  style={{
                    background: isActive ? sc.color : 'rgba(255, 255, 255, 0.05)',
                    color: isActive ? '#0f172a' : '#f8fafc',
                    border: `1px solid ${isActive ? sc.color : 'rgba(255, 255, 255, 0.1)'}`,
                    borderRadius: '8px',
                    padding: '0.4rem 0.85rem',
                    fontSize: '0.8rem',
                    fontWeight: 800,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {sc.badge}
                </button>
              );
            })}
          </div>

          {/* Selector de Horizonte */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(255,255,255,0.04)', padding: '2px 4px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
            {[7, 14, 30].map(h => (
              <button
                key={h}
                onClick={() => setHorizonDays(h)}
                style={{
                  background: horizonDays === h ? '#38bdf8' : 'transparent',
                  color: horizonDays === h ? '#0f172a' : 'rgba(255,255,255,0.7)',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '3px 9px',
                  fontSize: '0.74rem',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                +{h} días
              </button>
            ))}
          </div>
        </div>

        {/* Descripción del Escenario */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: '12px',
          padding: '0.85rem 1rem',
          marginBottom: '1.25rem'
        }}>
          <div style={{ fontSize: '0.86rem', fontWeight: 800, color: selectedScenario.color, marginBottom: '2px' }}>
            {selectedScenario.name}
          </div>
          <div style={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.4 }}>
            {selectedScenario.desc}
          </div>
        </div>

        {/* Gráfico de Proyección Recharts */}
        <div style={{ width: '100%', height: '240px', marginBottom: '1.25rem' }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={simulationData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.06)" />
              <XAxis dataKey="day" stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 11 }} />
              <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  background: 'rgba(15, 23, 42, 0.95)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '8px',
                  fontSize: '0.75rem',
                  color: '#fff'
                }}
              />
              <Legend wrapperStyle={{ fontSize: '0.72rem', paddingTop: '6px' }} />

              {/* TSB del escenario activo */}
              <Line
                type="monotone"
                dataKey={`tsb${activeScenario}`}
                name={`TSB Proyectado (${selectedScenario.badge})`}
                stroke={selectedScenario.color}
                strokeWidth={2.5}
                dot={{ r: 2 }}
              />

              {/* CTL del escenario activo */}
              <Line
                type="monotone"
                dataKey={`ctl${activeScenario}`}
                name="Fitness CTL"
                stroke="#38bdf8"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={false}
              />

              {/* ATL del escenario activo */}
              <Line
                type="monotone"
                dataKey={`atl${activeScenario}`}
                name="Fatiga ATL"
                stroke="#f43f5e"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Resumen de Snapshots Proyectados */}
        {finalPoint && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '0.75rem',
            marginBottom: '1rem'
          }}>
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '0.65rem 0.85rem' }}>
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>TSB en +{horizonDays}d</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 900, color: finalPoint[`tsb${activeScenario}`] >= 0 ? '#10b981' : '#f59e0b' }}>
                {finalPoint[`tsb${activeScenario}`] > 0 ? `+${finalPoint[`tsb${activeScenario}`]}` : finalPoint[`tsb${activeScenario}`]}
              </div>
            </div>

            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '0.65rem 0.85rem' }}>
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>CTL en +{horizonDays}d</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 900, color: '#38bdf8' }}>
                {finalPoint[`ctl${activeScenario}`]}
              </div>
            </div>

            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '0.65rem 0.85rem' }}>
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>ATL en +{horizonDays}d</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 900, color: '#f43f5e' }}>
                {finalPoint[`atl${activeScenario}`]}
              </div>
            </div>
          </div>
        )}

        {/* Disclaimer No Médico */}
        <div style={{
          background: 'rgba(245, 158, 11, 0.07)',
          border: '1px solid rgba(245, 158, 11, 0.2)',
          borderRadius: '10px',
          padding: '0.65rem 0.85rem',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Info size={16} color="#f59e0b" style={{ flexShrink: 0 }} />
          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.35 }}>
            Proyección matemática basada en el modelo Impulse-Response (Banister/Coggan). No constituye prescripción médica ni garantía de rendimiento biológico.
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalNode, document.body) : modalNode;
}
